import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import saveAs from 'file-saver';
import JSZip from 'jszip';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { buildBprEventLogTextFile, type BprEventLogSnapshot, type BprLogIndex } from '../../../robots/BprToyLogs';
import { formatErrorMessage } from '../../../utils/formatErrorMessage';

// Temporarily disabled for BPS-720 launch: clearing logs can trigger a catastrophic
// firmware issue on some electronics. Re-enable only after the firmware fix is released and validated.
const ENABLE_BPR_LOG_CLEARING = false;

interface DULogsDialogProps {
  open: boolean;
  onClose: () => void;
}

function getAbortError(): DOMException {
  return new DOMException('The operation was aborted.', 'AbortError');
}

const DULogsDialog = ({ open, onClose }: DULogsDialogProps) => {
  const { t } = useTranslation();
  const [logs, setLogs] = useState<BprEventLogSnapshot[]>([]);
  const [selectedLogIndex, setSelectedLogIndex] = useState<BprLogIndex | null>(null);
  const [loading, setLoading] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const activeRequestControllerRef = useRef<AbortController | null>(null);

  const activeLog = useMemo(() => logs.find((log) => log.source.index === selectedLogIndex) ?? logs[0] ?? null, [logs, selectedLogIndex]);

  const logsWithClearableStatus = useMemo(() => logs.filter((log) => log.status), [logs]);

  const [clearAllConfirmOpen, setClearAllConfirmOpen] = useState(false);

  const cancelPendingRequest = useCallback(() => {
    if (activeRequestControllerRef.current && !activeRequestControllerRef.current.signal.aborted) {
      activeRequestControllerRef.current.abort(getAbortError());
    }
    activeRequestControllerRef.current = null;
  }, []);

  const beginRequest = useCallback(() => {
    cancelPendingRequest();
    const controller = new AbortController();
    activeRequestControllerRef.current = controller;
    return controller;
  }, [cancelPendingRequest]);

  const finishRequest = useCallback((controller: AbortController) => {
    if (activeRequestControllerRef.current === controller) {
      activeRequestControllerRef.current = null;
    }
  }, []);

  const loadLogs = useCallback(
    async (preferredIndex: BprLogIndex | null = null, requestController: AbortController = beginRequest()) => {
      const { signal } = requestController;
      setLoading(true);
      setError(null);

      try {
        const nextLogs = (await window.robotService?.getBprEventLogs?.(signal)) ?? [];
        if (signal.aborted) {
          return;
        }
        setLogs(nextLogs);
        setSelectedLogIndex((current: BprLogIndex | null) => {
          const desired = preferredIndex ?? current;
          if (desired && nextLogs.some((log) => log.source.index === desired)) {
            return desired;
          }
          return nextLogs[0]?.source.index ?? null;
        });
      } catch (loadError) {
        if (signal.aborted) {
          return;
        }
        setLogs([]);
        setSelectedLogIndex(null);
        setError(formatErrorMessage(loadError));
      } finally {
        finishRequest(requestController);
        if (!signal.aborted) {
          setLoading(false);
        }
      }
    },
    [beginRequest, finishRequest]
  );

  useEffect(() => {
    if (!open) {
      cancelPendingRequest();
      setClearAllConfirmOpen(false);
      return;
    }

    void loadLogs();
  }, [cancelPendingRequest, loadLogs, open]);

  useEffect(() => {
    return () => {
      cancelPendingRequest();
    };
  }, [cancelPendingRequest]);

  const handleClearAllLogs = async () => {
    const clearLogs = logsWithClearableStatus;
    if (clearLogs.length === 0 || !window.robotService?.clearBprEventLog) {
      return;
    }

    setClearAllConfirmOpen(false);

    const requestController = beginRequest();
    const { signal } = requestController;
    setClearing(true);
    setError(null);

    const preferredIndexAfterLoad = selectedLogIndex ?? activeLog?.source.index ?? null;

    try {
      for (const log of clearLogs) {
        if (signal.aborted) {
          return;
        }
        await window.robotService.clearBprEventLog(log.source.index, signal);
      }
      if (signal.aborted) {
        return;
      }
      await loadLogs(preferredIndexAfterLoad, requestController);
    } catch (clearError) {
      if (signal.aborted) {
        return;
      }
      setError(formatErrorMessage(clearError));
    } finally {
      finishRequest(requestController);
      if (!signal.aborted) {
        setClearing(false);
      }
    }
  };

  const handleDownloadLogs = async () => {
    if (logs.length === 0) {
      return;
    }

    try {
      const zip = new JSZip();
      logs.forEach((log) => {
        zip.file(`${log.source.filename}.txt`, buildBprEventLogTextFile(log));
      });
      const content = await zip.generateAsync({ type: 'blob' });
      saveAs(content, 'drive-unit-logs.zip');
    } catch (downloadError) {
      setError(formatErrorMessage(downloadError));
    }
  };

  return (
    <>
      {ENABLE_BPR_LOG_CLEARING && (
        <Dialog open={clearAllConfirmOpen} onClose={() => setClearAllConfirmOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle variant="dialogTitle" align="center">
            <Trans i18nKey="DULogsDialog.clear-all-confirm.title">Clear all logs?</Trans>
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ py: 3, px: 3 }}>
            <Typography variant="body1" component="p">
              <Trans i18nKey="DULogsDialog.clear-all-confirm.body">
                This clears every drive unit log buffer (all tabs), not only the log you are viewing. This cannot be undone.
              </Trans>
            </Typography>
          </DialogContent>
          <Divider />
          <DialogActions sx={{ justifyContent: 'center', px: 2, py: 2 }}>
            <Stack direction="row" spacing={2}>
              <Button variant="text" onClick={() => setClearAllConfirmOpen(false)}>
                <Trans i18nKey="DULogsDialog.clear-all-confirm.cancel">Cancel</Trans>
              </Button>
              <Button variant="contained" color="error" onClick={() => void handleClearAllLogs()}>
                <Trans i18nKey="DULogsDialog.clear-all-confirm.confirm">Clear all logs</Trans>
              </Button>
            </Stack>
          </DialogActions>
        </Dialog>
      )}

      <Box position="relative" px={3} pt={3}>
        <IconButton aria-label={t('aria-label.close-dialog', 'close dialog')} onClick={onClose} sx={{ position: 'absolute', right: 16, top: 16 }}>
          <CloseIcon />
        </IconButton>
        <DialogTitle align="center" sx={{ pt: 0, px: 0 }}>
          <Trans i18nKey="DULogsDialog.title">Drive Unit Logs</Trans>
        </DialogTitle>
      </Box>
      <Divider />
      <DialogContent sx={{ px: 3, py: 2 }}>
        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        {logs.length > 0 && (
          <Tabs
            value={activeLog?.source.index ?? false}
            onChange={(_event, value: BprLogIndex) => setSelectedLogIndex(value)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ mb: 2 }}
          >
            {logs.map((log) => (
              <Tab key={log.source.index} value={log.source.index} label={log.source.label} />
            ))}
          </Tabs>
        )}

        <Paper elevation={2} sx={{ minHeight: 320, maxHeight: 420, overflow: 'auto', p: 3 }}>
          {loading ? (
            <Box display="flex" alignItems="center" justifyContent="center" minHeight={280} gap={2}>
              <CircularProgress size={24} />
              <Typography>
                <Trans i18nKey="DULogsDialog.loading">Loading logs…</Trans>
              </Typography>
            </Box>
          ) : activeLog ? (
            <>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                {activeLog.status ? (
                  <Trans i18nKey="DULogsDialog.stats">
                    Used {{ used: activeLog.status.logUsed }} / {{ capacity: activeLog.status.logCapacity }} bytes,{' '}
                    {{ events: activeLog.status.loggedEvents }} events
                  </Trans>
                ) : (
                  <Trans i18nKey="DULogsDialog.status-unavailable">Log status unavailable</Trans>
                )}
              </Typography>

              {activeLog.error ? (
                <Typography color="error">{activeLog.error}</Typography>
              ) : activeLog.text ? (
                <Typography component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0 }}>
                  {activeLog.text}
                </Typography>
              ) : (
                <Typography textAlign="center">
                  <Trans i18nKey="DULogsDialog.no-logs">No logs to display for this source</Trans>
                </Typography>
              )}
            </>
          ) : (
            <Typography textAlign="center">
              <Trans i18nKey="DULogsDialog.unavailable">Drive unit log access is unavailable.</Trans>
            </Typography>
          )}
        </Paper>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 0, justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Button variant="outlined" onClick={() => void loadLogs()} disabled={loading || clearing}>
          <Trans i18nKey="DULogsDialog.button.refresh-logs">Refresh Logs</Trans>
        </Button>
        {ENABLE_BPR_LOG_CLEARING && (
          <Button
            variant="contained"
            onClick={() => {
              if (logsWithClearableStatus.length === 0) return;
              setClearAllConfirmOpen(true);
            }}
            disabled={logsWithClearableStatus.length === 0 || loading || clearing}
          >
            <Trans i18nKey="DULogsDialog.button.clear-all-logs">Clear All Logs</Trans>
          </Button>
        )}
        <Button variant="contained" onClick={() => void handleDownloadLogs()} disabled={logs.length === 0 || loading || clearing}>
          <Trans i18nKey="DULogsDialog.button.download-logs">Download All Logs</Trans>
        </Button>
      </DialogActions>
    </>
  );
};

export default DULogsDialog;
